/* eslint-disable no-restricted-globals */
/* eslint-disable prefer-template */
import { useRoute } from 'vue-router';
import { useNamespace } from '@ibiz-template/vue3-util';
import { defineComponent, onMounted, ref, Ref } from 'vue';
import { CoreConst, setAppCookie } from '@ibiz-template/core';
import { isDingDing, isWeChat } from '@ibiz-template/runtime';
import { AndroidPlatformProvider, DownloadType } from '../../platform';
import './download-view.scss';

/**
 * @description 路由参数
 * @interface IRouteParams
 */
interface IRouteParams {
  /**
   * @description 下载类型
   * @type {DownloadType}
   * @memberof IRouteParams
   */
  type: DownloadType;

  /**
   * @description TOKEN
   * @type {string}
   * @memberof IRouteParams
   */
  token: string;

  /**
   * @description 下载数据 JSON字符串
   * @type {string}
   * @memberof IRouteParams
   */
  data: string;
}

export const DownloadView = defineComponent({
  setup() {
    const ns = useNamespace('download-view');

    const route = useRoute();

    const ua = window.navigator.userAgent.toLowerCase();

    /**
     * 是否为安卓内置WebView环境
     */
    const isAndroidWebView =
      /Android|Adr/i.test(ua) && /wv/i.test(ua) && /version\/4\.0/i.test(ua);

    /**
     * 是否为第三方环境
     */
    const isThirdParty = isWeChat() || isDingDing();

    /**
     * 是否显示提示
     */
    const showTip: Ref<boolean> = ref(isThirdParty || isAndroidWebView);

    /**
     * 提示信息
     */
    const tooltip: Ref<string> = ref('');

    /**
     * 状态
     */
    const status: Ref<string> = ref('init');

    /**
     * 下载参数
     */
    const params: IRouteParams = {
      token: route.query.token as string,
      data: route.query.data as string,
      type: route.query.type as DownloadType,
    };

    // 设置TOKEN
    if (params.token) setAppCookie(CoreConst.TOKEN, params.token, 0);

    const onDownload = async () => {
      status.value = 'processing';
      tooltip.value = ibiz.i18n.t('view.downloading');
      let result: boolean = false;
      try {
        const data = JSON.parse(params.data);
        switch (params.type) {
          case DownloadType.DOWNLOAD:
            result = await ibiz.platform.download(data.url, data.fileName);
            break;
          case DownloadType.BACKEND_EXPORT:
            result = await ibiz.platform.backendExport(data);
            break;
          case DownloadType.FRONT_EXPORT:
            result = await ibiz.platform.frontExport(data);
            break;
          default:
            break;
        }
      } catch (error) {
        ibiz.message.error(error as string);
      }
      status.value = result ? 'success' : 'error';
      tooltip.value = result
        ? ibiz.i18n.t('view.downloadSuccess')
        : ibiz.i18n.t('view.downloadFailed');
    };

    if (showTip.value) {
      tooltip.value = isThirdParty
        ? ibiz.i18n.t('view.noSupportDownloadTip')
        : ibiz.i18n.t('view.noSupportDownload');
      status.value = 'disabled';
    } else {
      onDownload();
    }

    /**
     * @description 绘制提示
     * @returns {*}
     */
    const renderTip = () => {
      switch (status.value) {
        case 'success':
          return (
            <img class={ns.b('success-img')} src='./assets/img/success.png' />
          );
        case 'disabled':
        case 'error':
          return <img class={ns.b('error-img')} src='./assets/img/error.png' />;
        case 'processing':
          return <van-loading type='spinner' />;
        default:
          break;
      }
    };

    /**
     * @description 绘制按钮
     * @returns {*}
     */
    const renderButton = () => {
      // 是否为安卓应用环境
      if (isAndroidWebView && !isThirdParty)
        return (
          <van-button
            type='primary'
            onClick={() =>
              (ibiz.platform as AndroidPlatformProvider).openBrowser(
                window.location.href,
              )
            }
          >
            {ibiz.i18n.t('view.browserDownload')}
          </van-button>
        );
      if (['init', 'success', 'error'].includes(status.value) && params.data)
        return (
          <van-button onClick={onDownload} type='primary'>
            {['success', 'error'].includes(status.value)
              ? ibiz.i18n.t('view.reDownload')
              : ibiz.i18n.t('view.immediatelyDownload')}
          </van-button>
        );
    };

    onMounted(() => {
      ibiz.util.hiddenAppLoading();
    });

    return {
      ns,
      tooltip,
      status,
      renderTip,
      onDownload,
      renderButton,
    };
  },
  render() {
    return (
      <div
        class={[
          this.ns.b(),
          this.ns.is('disabled', this.status === 'disabled'),
        ]}
      >
        <div class={this.ns.e('tip')}>{this.renderTip()}</div>
        <div class={this.ns.e('tooltip')}>{this.tooltip}</div>
        {this.renderButton()}
      </div>
    );
  },
});
